// src/pages/app/SystemControls.js
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../../lib/firebase";
import useUserRole from "../../hooks/useUserRole";
import useRolePermissions from "../../hooks/useRolePermissions";
import AdminPageHeader from "../../components/AdminPageHeader";
import FieldAccessSettings from "../../components/FieldAccessSettings";

const GENERAL_CONFIG_REF = doc(db, "appConfig", "general");

export default function SystemControls() {
  const { role, loading } = useUserRole();
  const { can, loading: permsLoading } = useRolePermissions(role);

  const [schedulePublished, setSchedulePublished] = useState(false);
  const [statusLoading, setStatusLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let mounted = true;

    async function loadStatus() {
      try {
        setStatusLoading(true);
        const snap = await getDoc(GENERAL_CONFIG_REF);
        const published = snap.exists()
          ? !!snap.data()?.schedulePublished
          : false;

        if (mounted) setSchedulePublished(published);
      } catch (err) {
        console.error("Failed to load schedulePublished:", err);
        if (mounted) setSchedulePublished(false);
      } finally {
        if (mounted) setStatusLoading(false);
      }
    }

    loadStatus();

    return () => {
      mounted = false;
    };
  }, []);

  async function handleSetPublished(nextValue) {
    setBusy(true);

    try {
      await setDoc(
        GENERAL_CONFIG_REF,
        {
          schedulePublished: !!nextValue,
          updatedAt: new Date(),
        },
        { merge: true }
      );

      setSchedulePublished(!!nextValue);
    } catch (err) {
      console.error("Failed to update schedulePublished:", err);
      alert("Could not update schedule status. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  if (loading || permsLoading) {
    return <div style={{ padding: 20 }}>Loading…</div>;
  }

  if (!can("ScheduleControl")) {
    return (
      <div style={{ padding: 20 }}>
        <AdminPageHeader title="System Controls" />
        <p>You do not have access to this page.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: 20 }}>
      <AdminPageHeader title="System Controls" />

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: 16,
        }}
      >
        <div
          className="adminModuleCard"
          style={{
            textDecoration: "none",
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          <div className="adminModuleTitle">Publish / Lock Schedule</div>
          <div className="adminModuleHint">
            Control schedule visibility for staff dashboards and schedule views
          </div>

          <div
            style={{
              marginTop: 4,
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              width: "fit-content",
              padding: "6px 10px",
              borderRadius: 999,
              border: "1px solid #d1d5db",
              background: schedulePublished ? "#ecfdf5" : "#fff7ed",
              color: "#0f172a",
              fontWeight: 700,
              fontSize: 14,
            }}
          >
            Status:{" "}
            {statusLoading
              ? "Loading…"
              : schedulePublished
              ? "Published"
              : "Unpublished"}
          </div>

          <div
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              marginTop: 4,
            }}
          >
            <button
              className="btn"
              onClick={() => handleSetPublished(true)}
              disabled={busy || statusLoading || schedulePublished}
            >
              {busy && !schedulePublished ? "Working…" : "Publish Schedule"}
            </button>

            <button
              className="btn btn-secondary"
              onClick={() => handleSetPublished(false)}
              disabled={busy || statusLoading || !schedulePublished}
            >
              {busy && schedulePublished ? "Working…" : "Lock Schedule"}
            </button>
          </div>
        </div>

        {can("PayRates") && (
          <Link to="/admin/system/pay-rates" className="adminModuleCard">
            <div className="adminModuleTitle">Set Pay Rates</div>
            <div className="adminModuleHint">Manage employee pay rates</div>
          </Link>
        )}

        {can("PermissionsEdit") && (
          <Link to="/admin/permissions" className="adminModuleCard">
            <div className="adminModuleTitle">Permissions</div>
            <div className="adminModuleHint">Roles & access control</div>
          </Link>
        )}

        {can("TournamentDates") && (
          <Link to="/admin/tournament-dates" className="adminModuleCard">
            <div className="adminModuleTitle">Tournament Dates</div>
            <div className="adminModuleHint">Configure tournament week</div>
          </Link>
        )}
      </div>

      <FieldAccessSettings />
    </div>
  );
}